import React, { useState } from 'react';
import { App, Form, Input, Button, Alert } from 'antd';
import { UserOutlined, LockOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { login, loginWithGoogle, clearError, GoogleAuthError } from '../../store/slices/authSlice';
import { LoginRequest, AUTH_ERROR_CODES } from '../../types/auth';
import { isGoogleAuthEnabled } from '../../config/google';
import GoogleSignInButton from './GoogleSignInButton';
import LoginBackground from './loginBackground/LoginBackground';
import Showcase from '../showcase/Showcase';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import LegalDocumentModal from './LegalDocumentModal';
import { TERMS_VERSION, PRIVACY_VERSION, termsContent, privacyContent } from '../../content/legal';
import './auth-theme.css';
import styles from './LoginForm.module.css';

// Desde este ancho la presentación ocupa la columna izquierda; abajo de él, el
// celular ve una animación de fondo (al azar en cada visita) detrás del
// formulario. LoginForm.module.css
// usa la misma query (negada) para el layout.
const SHOWCASE_QUERY = '(min-width: 900px)';

// Soporte por WhatsApp — mismo número usado en la landing-page (App.tsx:340)
const SUPPORT_WHATSAPP_URL = 'https://wa.me/522206294774';

interface LoginFormProps {
  onSwitchToRegister?: () => void;
}

const WhatsAppIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M.057 24l1.687-6.163a11.867 11.867 0 0 1-1.587-5.946C.16 5.335 5.495 0 12.05 0a11.817 11.817 0 0 1 8.413 3.488 11.824 11.824 0 0 1 3.48 8.414c-.003 6.557-5.338 11.892-11.893 11.892a11.9 11.9 0 0 1-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z" />
  </svg>
);

const LoginForm: React.FC<LoginFormProps> = ({ onSwitchToRegister }) => {
  // App.useApp() y no el import estatico: bajo React 19 el estatico no dibuja
  const { modal, message } = App.useApp();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { loading, error } = useAppSelector((state) => state.auth);
  const [form] = Form.useForm();
  const wide = useMediaQuery(SHOWCASE_QUERY);

  const [termsModalOpen, setTermsModalOpen] = useState(false);
  const [privacyModalOpen, setPrivacyModalOpen] = useState(false);
  // El botón de Google se oculta si su script no cargó (red bloqueada, extensión):
  // dejar un hueco con un separador que no separa nada se ve roto.
  const [googleAvailable, setGoogleAvailable] = useState(isGoogleAuthEnabled());

  // Clear error when user starts typing
  const handleFieldChange = () => {
    if (error) {
      dispatch(clearError());
    }
  };

  const handleGoogleCredential = async (idToken: string) => {
    try {
      await dispatch(loginWithGoogle({ idToken })).unwrap();
      // Navegación manejada por App con base en el auth state
    } catch (err) {
      const googleError = err as GoogleAuthError;

      // Correo sin cuenta: en vez de un error, se sigue al registro con el perfil de
      // Google y el mismo token puestos. Así "entrar con Google" nunca es un callejón
      // sin salida para quien todavía no se ha dado de alta.
      if (googleError?.code === AUTH_ERROR_CODES.googleNoAccount) {
        navigate('/register', { state: { googleCredential: idToken, googleProfile: googleError.profile } });
        return;
      }

      // El resto ya quedó en state.auth.error → <Alert/>; el toast es para que se note
      // sin tener que mirar arriba, porque el clic ocurrió abajo en el botón.
      if (googleError?.message) {
        message.error(googleError.message);
      }
    }
  };

  const handleSubmit = async (values: LoginRequest) => {
    try {
      const result = await dispatch(login(values)).unwrap();

      // Notificar al usuario si se cerraron otras sesiones automáticamente
      if (result.hasClosedOtherSessions) {
        const sessionText =
          result.closedSessionsCount === 1
            ? 'otra sesión activa fue'
            : `${result.closedSessionsCount} sesiones activas fueron`;

        modal.info({
          title: 'Sesión Cerrada en Otro Dispositivo',
          icon: <InfoCircleOutlined style={{ color: '#1890ff' }} />,
          content: (
            <div>
              <p>Has iniciado sesión exitosamente.</p>
              <p>
                <strong>{sessionText} cerrada(s) automáticamente</strong> porque
                solo se permite una sesión activa por usuario.
              </p>
              <p style={{ marginTop: '12px', fontSize: '12px', color: 'var(--text-secondary)' }}>
                Si no reconoces esta actividad, te recomendamos cambiar tu
                contraseña inmediatamente.
              </p>
            </div>
          ),
          okText: 'Entendido',
        });
      }

      // Navegación manejada por App con base en el auth state
    } catch {
      // Error manejado por el slice (state.auth.error → <Alert/>)
    }
  };

  return (
    <div className={styles.container}>
      {/* Sólo se monta la variante visible: la otra tendría animaciones corriendo sin verse */}
      {wide ? (
        <aside className={styles.showcase}>
          <Showcase className={styles.showcaseInner} />
        </aside>
      ) : (
        <LoginBackground />
      )}

      <div className={styles.panel}>
        <header className={styles.nav}>
          <a
            className={styles.help}
            href={SUPPORT_WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            <WhatsAppIcon />
            ¿Necesitas ayuda?
          </a>
        </header>

        <main className={styles.main}>
          <div className={styles.card}>
            <div className={styles.logoWrap}>
              <img src="/logo-icon.png" alt="Massrestaurant" className="app-logo" />
            </div>

            <h1 className={styles.title}>Iniciar sesión</h1>

            {error && (
              <Alert
                message={error}
                type="error"
                showIcon
                closable
                className={styles.alert}
                onClose={() => dispatch(clearError())}
              />
            )}

            <Form
              form={form}
              name="login"
              onFinish={handleSubmit}
              onValuesChange={handleFieldChange}
              layout="vertical"
              requiredMark={false}
              className={styles.form}
            >
              <Form.Item
                name="email"
                label="Email"
                rules={[
                  { required: true, message: 'Por favor ingresa tu email' },
                  { type: 'email', message: 'Ingresa un email válido' },
                ]}
              >
                <Input
                  prefix={<UserOutlined />}
                  placeholder="tu@restaurante.com"
                  autoComplete="email"
                />
              </Form.Item>

              <Form.Item
                name="password"
                label="Contraseña"
                rules={[
                  { required: true, message: 'Por favor ingresa tu contraseña' },
                  { min: 6, message: 'La contraseña debe tener al menos 6 caracteres' },
                ]}
              >
                <Input.Password
                  prefix={<LockOutlined />}
                  placeholder="••••••••"
                  autoComplete="current-password"
                />
              </Form.Item>

              <Form.Item style={{ marginBottom: 0 }}>
                <Button
                  type="primary"
                  htmlType="submit"
                  block
                  loading={loading}
                  className={styles.submit}
                >
                  Iniciar sesión
                </Button>
              </Form.Item>
            </Form>

            {googleAvailable && (
              <>
                <div className={styles.divider}>
                  <span>o</span>
                </div>
                <GoogleSignInButton
                  text="signin_with"
                  onCredential={handleGoogleCredential}
                  onUnavailable={() => setGoogleAvailable(false)}
                />
                {/* Aviso fijo, no una respuesta del servidor. Una cuenta creada con Google
                    no tiene contraseña, así que al intentarla sale "correo o contraseña
                    incorrectos" y no se entiende por qué. Decirlo aquí resuelve eso sin que
                    el login confirme, correo por correo, cuáles existen. */}
                <p className={styles.googleHint}>
                  ¿Creaste tu cuenta con Google? Entra por aquí: esa cuenta no tiene contraseña.
                </p>
              </>
            )}

            {onSwitchToRegister && (
              <p className={styles.switchWrap}>
                ¿No tienes cuenta?{' '}
                <Button type="link" onClick={onSwitchToRegister}>
                  Crear una cuenta
                </Button>
              </p>
            )}
          </div>
        </main>

        <footer className={styles.bottom}>
          <span>© {new Date().getFullYear()} Massrestaurant</span>
          <span className={styles.links}>
            <button type="button" onClick={() => setTermsModalOpen(true)}>
              Términos y Condiciones
            </button>
            <button type="button" onClick={() => setPrivacyModalOpen(true)}>
              Aviso de Privacidad
            </button>
          </span>
        </footer>
      </div>

      <LegalDocumentModal
        open={termsModalOpen}
        onClose={() => setTermsModalOpen(false)}
        title="Términos y Condiciones"
        version={TERMS_VERSION}
        content={termsContent}
      />
      <LegalDocumentModal
        open={privacyModalOpen}
        onClose={() => setPrivacyModalOpen(false)}
        title="Aviso de Privacidad"
        version={PRIVACY_VERSION}
        content={privacyContent}
      />
    </div>
  );
};

export default LoginForm;
